import { mutate } from 'swr';

import { unmountAngularLeaf } from 'app/angular-leaf.bootstrap';

import { endAnalyticsSession } from '../../../analytics/analytics-bridge';
import { clearSession } from '../../../http/session-cleanup';

const LOGIN_ROUTE = '/pages/login';

// Navegação por hash (useHash:true): alterar location.hash dispara o HashRouter
// do React, sem acoplar este efeito ao hook de navegação.
function navigateToLogin(): void {
  window.location.hash = `#${LOGIN_ROUTE}`;
}

/**
 * Descarta todo o cache do SWR sem revalidar.
 *
 * O cache vive no SWRConfig do AppProviders, que está acima da árvore e não
 * desmonta na mesma aba, e `swrConfig` usa `revalidateIfStale: false` — então
 * dado de uma sessão encerrada é servido ao próximo login em vez de rebuscado.
 * Chaves como `pessoas/pessoa_logada` não carregam identificador de usuário, de
 * modo que o login seguinte reaproveitaria a entrada do usuário anterior (nome,
 * foto, contas e o `primeiroAcesso` que decide a home).
 *
 * Limpa tudo em vez de uma lista de chaves: no logout a regra é que nenhum dado
 * da sessão sobreviva, e uma allowlist ficaria desatualizada a cada hook novo.
 */
function clearDataCache(): Promise<unknown> {
  return mutate(() => true, undefined, { revalidate: false });
}

/**
 * Encerra a sessão de analytics antes de qualquer teardown. O evento de logout
 * precisa sair enquanto o SDK ainda guarda a identidade de quem está saindo; depois
 * do reset ele sairia anônimo. Telemetria é melhor-esforço: uma falha aqui não pode
 * impedir a limpeza da sessão nem a ida ao login.
 */
function endAnalyticsSafely(): void {
  try {
    endAnalyticsSession();
  } catch {
    // Falha de telemetria é descartada de propósito.
  }
}

/**
 * Encerra a sessão pelo React (sem AuthService.logout via ponte): encerra a sessão
 * de analytics, limpa o storage compartilhado e o cache de dados, desmonta a folha
 * Angular para descartar seu estado em memória e overlays, e leva ao login. A
 * navegação acontece mesmo que a desmontagem falhe.
 */
export async function performLogout(): Promise<void> {
  endAnalyticsSafely();
  clearSession();
  await clearDataCache();
  try {
    await unmountAngularLeaf();
  } finally {
    navigateToLogin();
  }
}
